import React from 'react'
import { observer } from 'mobx-react'
import {
  SelectItem,
  SingleItemSelector,
} from 'admin/staffing/components/WorkspacePostingDialog/SingleItemSelector'
import Avatar from 'components/Avatar'
import Button from 'components/Button'
import {
  CustomSelect,
  ItemSource,
  useCustomSelectStateFromItemSource,
} from 'components/inputs/CustomSelect'
import {
  Form,
  FormColumn,
  FormColumns,
  FormGroupContainer,
  FormRow,
  TextAreaContainer,
  TextInputContainer,
} from 'components/inputs'
import {
  ModalSheet,
  ModalSheetContent,
  ModalSheetContentDescription,
  ModalSheetContentTitle,
  ModalSheetFooter,
  ModalSheetFooterButtonGroup,
  ModalSheetFooterSpacer,
  ModalSheetHeader,
  ModalSheetSection,
} from 'components/ModalSheet'
import { RadioButtonGroup, RadioButtonItem } from 'components/RadioButtonGroup'
import Skill from 'iam/models/Skill'
import { match } from 'utils/pattern-match-util'
import { pluralize } from 'utils/pluralization-util'
import { WorkspaceInvitationViewModel } from './WorkspaceInvitationViewModel'

interface CustomerInvitationFormProps {
  vm: WorkspaceInvitationViewModel
}

const CustomerInvitationForm = observer(
  ({ vm }: CustomerInvitationFormProps) => {
    return (
      <>
        <FormRow>
          <FormGroupContainer
            label="Email address"
            error={vm.customerValidation.getError('customerEmail')}
          >
            <TextInputContainer
              model={vm}
              field="customerEmail"
              type="email"
              placeholder="customer@example.com"
              disabled={vm.submit.pending}
              data-test="workspace-invitation-customer-email"
            />
          </FormGroupContainer>
        </FormRow>

        <FormRow>
          <FormColumns>
            <FormColumn>
              <FormGroupContainer
                label="First name"
                error={vm.customerValidation.getError('customerGivenName')}
              >
                <TextInputContainer
                  model={vm}
                  field="customerGivenName"
                  placeholder="John"
                  disabled={vm.submit.pending}
                  data-test="workspace-invitation-customer-given-name"
                />
              </FormGroupContainer>
            </FormColumn>
            <FormColumn>
              <FormGroupContainer
                label="Last name"
                error={vm.customerValidation.getError('customerFamilyName')}
              >
                <TextInputContainer
                  model={vm}
                  field="customerFamilyName"
                  placeholder="Doe"
                  disabled={vm.submit.pending}
                  data-test="workspace-invitation-customer-family-name"
                />
              </FormGroupContainer>
            </FormColumn>
          </FormColumns>
        </FormRow>

        <FormRow>
          <FormGroupContainer
            label="Phone number"
            sublabel="(optional)"
            error={vm.customerValidation.getError('customerPhoneNumber')}
          >
            <TextInputContainer
              model={vm}
              field="customerPhoneNumber"
              type="tel"
              placeholder="+1 555-123-4567"
              disabled={vm.submit.pending}
              data-test="workspace-invitation-customer-phone"
            />
          </FormGroupContainer>
        </FormRow>
      </>
    )
  },
)

interface RegularInvitationFormProps {
  vm: WorkspaceInvitationViewModel
}

const RegularInvitationForm = observer(({ vm }: RegularInvitationFormProps) => {
  return (
    <>
      <FormRow>
        <FormGroupContainer
          label="Email address"
          error={vm.invitationValidation.getError('emailAddressTextArea')}
        >
          <TextAreaContainer
            model={vm}
            field="emailAddressTextArea"
            rows={3}
            placeholder="e.g. timmy.taxes@taxfyle.com, cindy.cpa@firm.com"
            help="Enter one or more email addresses, separated by commas."
            data-test="workspace-invitation-email-textarea"
          />
        </FormGroupContainer>
      </FormRow>

      {vm.workspace && (
        <>
          <FormRow>
            <FormGroupContainer
              label="Role"
              error={vm.invitationValidation.getError('workspaceRole')}
            >
              <SingleItemSelector
                model={vm.workspaceRole}
                itemOptions={vm.workspaceRoles}
                onChange={(value) =>
                  vm.setWorkspaceRole(value === undefined ? null : value)
                }
                itemToString={(item) => (item ? item.name : '')}
                placeholder="Select workspace role"
                disabled={vm.submit.pending}
                dataTest="workspace-invitation-role-selector"
              />
            </FormGroupContainer>
          </FormRow>

          <FormRow>
            <FormGroupContainer label="Skills" sublabel="(optional)">
              <SkillsSelector
                vm={vm}
                disabled={vm.submit.pending}
                dataTest="workspace-invitation-skills-selector"
              />
            </FormGroupContainer>
          </FormRow>

          <FormRow>
            <FormGroupContainer label="Team" sublabel="(optional)">
              <SingleItemSelector
                model={vm.team}
                itemOptions={vm.teams}
                onChange={(value) =>
                  vm.setTeam(value === undefined ? null : value)
                }
                itemToString={(item) => (item ? item.name : '')}
                placeholder="Select team"
                disabled={vm.submit.pending}
                dataTest="workspace-invitation-team-selector"
              />
            </FormGroupContainer>
          </FormRow>

          {vm.team && (
            <FormRow>
              <FormGroupContainer
                label="Team role"
                error={vm.invitationValidation.getError('teamRole')}
              >
                <SingleItemSelector
                  model={vm.teamRole}
                  itemOptions={vm.teamRoles}
                  onChange={(value) =>
                    vm.setTeamRole(value === undefined ? null : value)
                  }
                  itemToString={(item) => (item ? item.name : '')}
                  placeholder="Select team role"
                  disabled={vm.submit.pending}
                  dataTest="workspace-invitation-team-role-selector"
                />
              </FormGroupContainer>
            </FormRow>
          )}
        </>
      )}

      <FormRow>
        <FormGroupContainer label="Portal">
          <SingleItemSelector
            model={vm.portal}
            itemOptions={vm.portals}
            onChange={(value) => (value ? vm.setPortal(value) : undefined)}
            itemToString={(item) => item}
            placeholder="Select portal to link to"
            disabled={vm.submit.pending}
            dataTest="workspace-invitation-portal-selector"
          />
        </FormGroupContainer>
      </FormRow>
    </>
  )
})

interface WorkspaceInvitationDialogProps {
  vm: WorkspaceInvitationViewModel
}

export const WorkspaceInvitationDialog = observer(
  ({ vm }: WorkspaceInvitationDialogProps) => {
    const renderForm = () =>
      match(vm.signInType, {
        AccessLink: () => <CustomerInvitationForm vm={vm} />,
        UsernamePassword: () => <RegularInvitationForm vm={vm} />,
      })

    return (
      <ModalSheet
        showing={vm.showing}
        onRequestClose={vm.close}
        disableCloseOnOutsideClick
      >
        <ModalSheetHeader withCloseButton />

        <ModalSheetContent>
          <ModalSheetSection horizontallySpaced>
            <ModalSheetContentTitle>Invite to workspace</ModalSheetContentTitle>
            <ModalSheetContentDescription compact>
              {vm.signInType === 'AccessLink'
                ? 'Fill in the customer information to allow access link-based sign in.'
                : "Fill in the emails of the users you'd like to invite, and complete the required information."}
            </ModalSheetContentDescription>
          </ModalSheetSection>

          <ModalSheetSection spaced>
            <Form id="workspaceInvitation" onSubmit={vm.submit}>
              <FormRow>
                <FormGroupContainer
                  label="Workspace"
                  error={
                    vm.signInType === 'AccessLink'
                      ? vm.customerValidation.getError('workspace')
                      : vm.invitationValidation.getError('workspace')
                  }
                >
                  <SingleItemSelector
                    model={vm.workspace}
                    itemOptions={vm.workspaces}
                    itemPrefix={(item) => (
                      <Avatar
                        subject={{
                          avatar: item?.branding.logos.icon,
                        }}
                        size={24}
                      />
                    )}
                    onChange={(value) =>
                      vm.setWorkspace(value === undefined ? null : value)
                    }
                    itemToString={(item) => (item ? item.name : '')}
                    placeholder="Select workspace"
                    disabled={vm.submit.pending}
                    dataTest="workspace-invitation-workspace-selector"
                  />
                </FormGroupContainer>
              </FormRow>

              {vm.workspace &&
                vm.canUseAccessLink &&
                vm.canUseUsernameAndPassword && (
                  <FormRow>
                    <RadioButtonGroup label="Sign-in Type" size="big-wide">
                      <RadioButtonItem
                        active={vm.signInType === 'UsernamePassword'}
                        onClick={() => vm.setSignInType('UsernamePassword')}
                        disabled={vm.submit.pending}
                      >
                        Username & Password
                      </RadioButtonItem>
                      <RadioButtonItem
                        active={vm.signInType === 'AccessLink'}
                        onClick={() => vm.setSignInType('AccessLink')}
                        disabled={vm.submit.pending}
                      >
                        Access Link
                      </RadioButtonItem>
                    </RadioButtonGroup>
                  </FormRow>
                )}

              {renderForm()}
            </Form>
          </ModalSheetSection>
        </ModalSheetContent>

        <ModalSheetFooter>
          <ModalSheetFooterSpacer />
          <ModalSheetFooterButtonGroup>
            <Button type="default" onClick={vm.close}>
              Close
            </Button>
            <Button
              submit
              form="workspaceInvitation"
              type="primary"
              disabled={vm.submit.pending}
              dataTest="workspace-invitation-submit"
            >
              {vm.signInType === 'AccessLink'
                ? 'Create new customer'
                : `Send ${pluralize('invite', vm.emailAddresses.length)}`}
            </Button>
          </ModalSheetFooterButtonGroup>
        </ModalSheetFooter>
      </ModalSheet>
    )
  },
)

interface SkillsSelectorProps {
  vm: WorkspaceInvitationViewModel
  disabled?: boolean
  dataTest?: string
}

const SkillsSelector = observer(function ({
  vm,
  disabled,
  dataTest,
}: SkillsSelectorProps) {
  const itemToString = (item: Skill | undefined) => item?.name || ''

  const itemSource = React.useMemo(
    () =>
      ItemSource.static(vm.availableSkills, (item, text) =>
        item.name.toLowerCase().includes(text.toLowerCase()),
      ),
    [vm.availableSkills],
  )

  const change = React.useCallback(
    (skills: Skill[]) => (skills ? vm.setSkills(skills) : undefined),
    [vm],
  )

  const itemProps = useCustomSelectStateFromItemSource({
    itemSource,
  })

  return (
    <CustomSelect
      {...itemProps}
      itemToString={itemToString}
      value={vm.skills}
      onChange={change}
      disabled={disabled}
      multi={true}
      persist
      placeholder="Select skills"
      data-test={dataTest}
      renderSearchItem={({ item, ...itemProps }) => (
        <SelectItem key={item.id} {...itemProps}>
          {itemToString(item)}
        </SelectItem>
      )}
    />
  )
})
